/**
 * Platform → Calendar — V6-07 §3, §4.
 *
 * Two OAuth apps, one per provider, pasted into a form and stored encrypted.
 * Protocol §5 in practice: no credential ever requires editing `.env`.
 *
 * Its own component rather than another block inside `PlatformSettings.tsx`,
 * which is already 600 lines and covers a different question — that card is
 * about *sending*, this one is about *authorising*. They share the same query
 * key, so they read one request between them.
 *
 * The redirect URIs are shown, copyable, and impossible to miss, because the
 * single most common way a setup fails is a redirect URI registered with a
 * trailing slash. Below them sits the connection log §4 asks for.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarSync, Check, Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError, api } from '@/lib/api';
import { describeError } from '@/lib/errorMessage';
import { writeOnlySecretValue } from './savePayload';

interface CalendarBlock {
  googleClientId: string | null;
  googleClientSecret: string | null;
  googleConfigured: boolean;
  microsoftClientId: string | null;
  microsoftClientSecret: string | null;
  microsoftConfigured: boolean;
  microsoftSecretExpiresAt: string | null;
  redirectUris: { google: string; microsoft: string };
}

interface CalendarLogRow {
  _id: string;
  provider: string;
  event: string;
  outcome: string;
  detail: string | null;
  userName: string;
  createdAt: string;
}

interface CalendarLogView {
  connections: {
    _id: string;
    provider: string;
    status: string;
    accountEmail: string;
    lastSyncAt: string | null;
    lastError: string | null;
  }[];
  rows: CalendarLogRow[];
}

const settingsKey = ['platform-settings'] as const;

function CopyableUri({ label, value, testId }: { label: string; value: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <div className="flex gap-1.5">
        <Input readOnly value={value} data-testid={testId} className="vt-mono text-[11px]" />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard
              .writeText(value)
              .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              })
              .catch(() => toast.error('Could not copy — select the text instead'));
          }}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </Button>
      </div>
    </div>
  );
}

export function PlatformCalendarCard() {
  const qc = useQueryClient();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [result, setResult] = useState<Record<string, { ok: boolean; summary: string }>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: settingsKey,
    retry: false,
    queryFn: async () => (await api.get<{ calendar: CalendarBlock }>('/platform/settings')).data,
  });

  const { data: log } = useQuery({
    queryKey: ['platform-calendar-log'],
    retry: false,
    queryFn: async () => (await api.get<CalendarLogView>('/platform/calendar/log')).data,
    // Somebody on this screen is usually mid-setup, waiting for the first row.
    refetchInterval: 15_000,
  });

  const save = useMutation({
    mutationFn: async () => {
      const googleSecret = writeOnlySecretValue(
        edits.googleClientSecret,
        Boolean(data?.calendar.googleClientSecret),
      );
      const microsoftSecret = writeOnlySecretValue(
        edits.microsoftClientSecret,
        Boolean(data?.calendar.microsoftClientSecret),
      );
      return (
        await api.patch<{ updated: string[] }>('/platform/settings', {
          calendar: {
            ...(edits.googleClientId !== undefined ? { googleClientId: edits.googleClientId } : {}),
            // Write-only fields go through the shared contract (V9.1 §1):
            // the sentinel to keep, `null` to clear, a typed value to replace.
            // An empty string is refused by the API and never produced here.
            ...(googleSecret !== undefined ? { googleClientSecret: googleSecret } : {}),
            ...(edits.microsoftClientId !== undefined
              ? { microsoftClientId: edits.microsoftClientId }
              : {}),
            ...(microsoftSecret !== undefined
              ? { microsoftClientSecret: microsoftSecret }
              : {}),
            ...(edits.microsoftSecretExpiresAt !== undefined
              ? { microsoftSecretExpiresAt: edits.microsoftSecretExpiresAt || null }
              : {}),
          },
        })
      ).data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: settingsKey });
      setEdits({});
      toast.success('Saved — the next Connect uses these credentials. No restart needed.');
    },
    onError: (err: unknown) =>
      toast.error(describeError(err, 'Could not save the calendar apps'), { duration: 15_000 }),
  });

  const test = useMutation({
    mutationFn: async (provider: 'google' | 'microsoft') =>
      (
        await api.post<{ ok: boolean; summary: string; redirectUri: string }>(
          '/platform/settings/calendar/test',
          { provider },
        )
      ).data,
    onSuccess: (data, provider) => {
      setResult((r) => ({ ...r, [provider]: { ok: data.ok, summary: data.summary } }));
      if (data.ok) toast.success(data.summary);
      else toast.error(data.summary, { duration: 15_000 });
    },
    onError: (err: unknown, provider) => {
      const summary = describeError(err, 'The test could not run');
      setResult((r) => ({ ...r, [provider]: { ok: false, summary } }));
      toast.error(summary, { duration: 15_000 });
    },
  });

  // Same rule as the credentials card: a denial renders nothing, anything else
  // has to say so rather than making the feature look absent.
  if (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return null;
    return null;
  }
  if (isLoading || !data) {
    return (
      <div className="vt-surface p-4">
        <Skeleton className="h-32" />
      </div>
    );
  }

  const calendar = data.calendar;
  const field = (key: string, stored: string | null) => edits[key] ?? stored ?? '';

  const providerBlock = (
    provider: 'google' | 'microsoft',
    label: string,
    configured: boolean,
    clientId: string | null,
    maskedSecret: string | null,
    redirectUri: string,
  ) => (
    <div
      className="flex flex-col gap-3 rounded-[8px] border border-[var(--border)] p-3"
      data-testid={`platform-calendar-${provider}`}
    >
      <div className="flex items-center gap-2">
        <h3 className="flex-1 text-[13px] font-medium">{label}</h3>
        <Badge tone={configured ? 'accent' : 'warn'}>
          {configured ? 'configured' : 'not configured'}
        </Badge>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${provider}ClientId`}>Client ID</Label>
          <Input
            id={`${provider}ClientId`}
            data-testid={`platform-calendar-${provider}-id`}
            autoComplete="off"
            value={field(`${provider}ClientId`, clientId)}
            onChange={(e) => setEdits((s) => ({ ...s, [`${provider}ClientId`]: e.target.value }))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${provider}ClientSecret`}>Client secret</Label>
          <Input
            id={`${provider}ClientSecret`}
            data-testid={`platform-calendar-${provider}-secret`}
            type="password"
            autoComplete="off"
            placeholder={maskedSecret ?? 'Not set'}
            value={edits[`${provider}ClientSecret`] ?? ''}
            onChange={(e) =>
              setEdits((s) => ({ ...s, [`${provider}ClientSecret`]: e.target.value }))
            }
          />
          {maskedSecret && (
            <span className="text-[11px] vt-muted" data-testid={`platform-calendar-${provider}-stored`}>
              Stored as {maskedSecret}. Typing here replaces it.
            </span>
          )}
        </div>
      </div>

      <CopyableUri
        label="Authorised redirect URI — paste this into the app registration"
        value={redirectUri}
        testId={`platform-calendar-${provider}-redirect`}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          data-testid={`platform-calendar-${provider}-test`}
          disabled={test.isPending}
          onClick={() => test.mutate(provider)}
        >
          {test.isPending ? <Loader2 className="animate-spin" /> : null}
          Send test sync
        </Button>
        {result[provider] && (
          <p
            className="text-[12px]"
            data-testid={`platform-calendar-${provider}-result`}
            data-outcome={result[provider].ok ? 'ok' : 'failed'}
            style={{ color: result[provider].ok ? 'var(--accent)' : 'var(--danger)' }}
          >
            {result[provider].summary}
          </p>
        )}
      </div>
    </div>
  );

  return (
    <section className="vt-surface flex flex-col gap-3 p-4" data-testid="platform-calendar">
      <div className="flex items-center gap-2">
        <CalendarSync className="size-4 vt-muted" />
        <h2 className="flex-1 text-[15px] font-semibold">Calendar sync apps</h2>
      </div>
      <p className="text-[12px] vt-muted">
        One Google Cloud project and one Microsoft Entra registration serve every workspace. Until
        both are filled in, the matching Connect button in Settings → Calendar tells people the
        provider is unavailable. Step-by-step setup is in{' '}
        <span className="vt-mono">docs/CALENDAR_APPS_SETUP.md</span>.
      </p>

      {providerBlock(
        'google',
        'Google Calendar',
        calendar.googleConfigured,
        calendar.googleClientId,
        calendar.googleClientSecret,
        calendar.redirectUris.google,
      )}

      {providerBlock(
        'microsoft',
        'Microsoft 365 / Outlook',
        calendar.microsoftConfigured,
        calendar.microsoftClientId,
        calendar.microsoftClientSecret,
        calendar.redirectUris.microsoft,
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="microsoftSecretExpiresAt">
          Microsoft client secret expires{' '}
          <span className="font-normal vt-muted">— set a calendar reminder for this date</span>
        </Label>
        <Input
          id="microsoftSecretExpiresAt"
          type="date"
          className="w-52"
          data-testid="platform-calendar-ms-expiry"
          value={
            edits.microsoftSecretExpiresAt ?? calendar.microsoftSecretExpiresAt?.slice(0, 10) ?? ''
          }
          onChange={(e) => setEdits((s) => ({ ...s, microsoftSecretExpiresAt: e.target.value }))}
        />
        <span className="text-[11px] vt-muted">
          An Entra secret expires on a date the portal shows once. The day it lapses, every
          Microsoft connection starts failing at once.
        </span>
      </div>

      <Button
        variant="primary"
        size="sm"
        className="self-start"
        data-testid="save-platform-calendar"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? <Loader2 className="animate-spin" /> : <Check />}
        Save calendar apps
      </Button>

      {/* ── §4: the connection log ───────────────────────────────── */}
      <div className="flex flex-col gap-2 border-t border-[var(--border)] pt-3">
        <h3 className="text-[13px] font-medium">Recent connection activity</h3>
        {log?.connections.length ? (
          <ul className="flex flex-col gap-1" data-testid="platform-calendar-connections">
            {log.connections.map((connection) => (
              <li key={connection._id} className="flex flex-wrap items-center gap-2 text-[12px]">
                <Badge tone={connection.status === 'connected' ? 'accent' : 'warn'}>
                  {connection.status}
                </Badge>
                <span className="vt-mono">{connection.provider}</span>
                <span>{connection.accountEmail}</span>
                <span className="vt-muted">
                  {connection.lastError ?? `last sync ${connection.lastSyncAt ?? 'never'}`}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[12px] vt-muted" data-testid="platform-calendar-no-connections">
            Nobody has connected a calendar yet.
          </p>
        )}

        {log?.rows.length ? (
          <ul
            className="mt-1 flex max-h-64 flex-col gap-0.5 overflow-y-auto rounded-[8px] border border-[var(--border)] p-2"
            data-testid="platform-calendar-log"
          >
            {log.rows.map((row) => (
              <li key={row._id} className="flex flex-wrap gap-1.5 text-[11px]" data-testid="platform-calendar-log-row">
                <span className="vt-mono vt-muted">{row.createdAt.slice(0, 19).replace('T', ' ')}</span>
                <span className="font-medium">{row.userName}</span>
                <span className="vt-mono">{row.provider}</span>
                <span
                  style={{
                    color:
                      row.outcome === 'failed'
                        ? 'var(--danger)'
                        : row.outcome === 'skipped'
                          ? 'var(--warn)'
                          : 'var(--accent)',
                  }}
                >
                  {row.event}
                </span>
                <span className="vt-muted">{row.detail}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </section>
  );
}
